import { formatCurrency } from '../utils/mortgageCalculations';
import { calculateBuydownSchedule } from '../utils/mortgageCalculations';

function MonthlyCostSummary({ loanData, calculatedData, manualOverrides, setManualOverrides }) {
    const buydownSchedule = calculateBuydownSchedule(
        calculatedData.loanAmount,
        loanData.interestRate,
        loanData.loanYears,
        loanData.buydownType
    );

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Monthly Payment Summary</h3>
            </div>

            {/* Main Summary Cards */}
            <div className="summary-grid">
                <div className="summary-card">
                    <div className="summary-label">Loan Amount</div>
                    <div className="summary-value">{formatCurrency(calculatedData.loanAmount)}</div>
                </div>

                <div className="summary-card">
                    <div className="summary-label">Total Monthly Payment</div>
                    <div className="summary-value">{formatCurrency(calculatedData.totalMonthly)}</div>
                    <div className="summary-subtext">Principal + Interest + Escrow</div>
                </div>
            </div>

            {/* Buydown Schedule */}
            {loanData.buydownType !== 'none' && (
                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                    <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--text-primary)' }}>
                        Buydown Payment Schedule
                    </h4>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Interest Rate</th>
                                    <th>Monthly P&I</th>
                                    <th>With Escrow</th>
                                </tr>
                            </thead>
                            <tbody>
                                {buydownSchedule.map((period, index) => (
                                    <tr key={index} className={index === 0 ? 'highlight' : ''}>
                                        <td>{period.period}</td>
                                        <td>{period.rate.toFixed(3)}%</td>
                                        <td>{formatCurrency(period.payment)}</td>
                                        <td>{formatCurrency(period.payment + calculatedData.escrow)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Payment Breakdown */}
            <div>
                <h4 style={{ marginBottom: 'var(--spacing-sm)', color: 'var(--text-primary)' }}>
                    Payment Breakdown
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Principal & Interest</span>
                        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{formatCurrency(calculatedData.monthlyPI)}</span>
                    </div>

                    {/* Helper function for editable fields */}
                    {/* Helper function for editable fields */}
                    {[
                        {
                            label: 'Property Tax',
                            field: 'propertyTax',
                            percentField: 'propertyTaxPercent',
                            autoValue: calculatedData.autoValues.propertyTax,
                            autoPercent: calculatedData.autoValues.propertyTaxPercent,
                            source: calculatedData.escrowDetails.propertyTaxDetails.source,
                            isPropertyTax: true
                        },
                        {
                            label: 'Homeowner Insurance',
                            field: 'insurance',
                            autoValue: calculatedData.autoValues.insurance,
                            source: calculatedData.escrowDetails.insuranceDetails.source
                        },
                        {
                            label: 'Private Mortgage Insurance (PMI)',
                            field: 'pmi',
                            autoValue: calculatedData.autoValues.pmi,
                            source: 'Down payment < 20%',
                            subtitle: true
                        }
                    ].map((item) => {
                        // Only show PMI if it's relevant (calculated > 0 OR overridden)
                        if (item.field === 'pmi' && item.autoValue === 0 && manualOverrides.pmi === null) return null;

                        const isOverridden = manualOverrides[item.field] !== null;

                        return (
                            <div key={item.field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <span style={{ color: 'var(--text-secondary)' }}>
                                        {item.label}
                                        {isOverridden && (
                                            <span className="badge" style={{ marginLeft: '0.5rem', fontSize: '0.65rem', background: 'var(--primary-color)', color: 'white' }}>Manual</span>
                                        )}
                                    </span>
                                    {!isOverridden && (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                            {item.subtitle ? item.source : `(${item.source})`}
                                        </span>
                                    )}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {/* Percentage Input for Property Tax */}
                                    {item.isPropertyTax && (
                                        <div className="input-group input-group-sm" style={{ width: '140px', marginRight: '0.5rem' }}>
                                            <input
                                                type="number"
                                                className="form-input"
                                                style={{ textAlign: 'right', paddingRight: '1.5rem' }}
                                                value={manualOverrides[item.percentField] !== null ? manualOverrides[item.percentField] : (item.autoPercent ? item.autoPercent.toFixed(3) : '')}
                                                onChange={(e) => {
                                                    const percent = e.target.value;
                                                    const amount = (loanData.homePrice * (parseFloat(percent) / 100)) / 12;
                                                    setManualOverrides(prev => ({
                                                        ...prev,
                                                        [item.percentField]: percent,
                                                        [item.field]: isNaN(amount) ? null : amount.toFixed(2)
                                                    }));
                                                }}
                                                onBlur={(e) => {
                                                    if (e.target.value === '') {
                                                        setManualOverrides(prev => ({ ...prev, [item.percentField]: null, [item.field]: null }));
                                                    }
                                                }}
                                            />
                                            <span className="input-suffix" style={{ right: '0.5rem' }}>%</span>
                                        </div>
                                    )}

                                    <div className="input-group input-group-sm" style={{ width: '120px' }}>
                                        <span className="input-prefix">$</span>
                                        <input
                                            type="number"
                                            className="form-input"
                                            style={{ textAlign: 'right', paddingRight: '0.5rem' }}
                                            value={isOverridden ? manualOverrides[item.field] : item.autoValue.toFixed(2)}
                                            onChange={(e) => {
                                                const amount = e.target.value;
                                                let updates = { [item.field]: amount };

                                                if (item.isPropertyTax) {
                                                    const percent = ((parseFloat(amount) * 12) / loanData.homePrice) * 100;
                                                    updates[item.percentField] = isNaN(percent) ? null : percent.toFixed(3);
                                                }

                                                setManualOverrides(prev => ({ ...prev, ...updates }));
                                            }}
                                            onBlur={(e) => {
                                                if (e.target.value === '') {
                                                    let updates = { [item.field]: null };
                                                    if (item.isPropertyTax) {
                                                        updates[item.percentField] = null;
                                                    }
                                                    setManualOverrides(prev => ({ ...prev, ...updates }));
                                                }
                                            }}
                                        />
                                    </div>
                                    {isOverridden && (
                                        <button
                                            className="btn-icon"
                                            onClick={() => {
                                                let updates = { [item.field]: null };
                                                if (item.isPropertyTax) {
                                                    updates[item.percentField] = null;
                                                }
                                                setManualOverrides(prev => ({ ...prev, ...updates }));
                                            }}
                                            title="Reset to calculated value"
                                            style={{
                                                padding: '0.25rem',
                                                color: 'var(--accent-color)',
                                                background: 'rgba(245, 87, 108, 0.1)',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: 'none',
                                                cursor: 'pointer',
                                                marginLeft: '0.25rem'
                                            }}
                                        >
                                            ↺
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
                        <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Total Escrow</span>
                        <span style={{ fontWeight: '700', color: 'var(--primary-color)' }}>{formatCurrency(calculatedData.escrow)}</span>
                    </div>
                </div>
            </div>

            {/* Info Alert */}
            <div className="alert alert-info" style={{ marginTop: 'var(--spacing-md)' }}>
                <strong>Note:</strong> Property tax and insurance estimates are based on {calculatedData.escrowDetails.propertyTaxDetails.state} state averages.
                You can adjust these values based on your actual costs.
            </div>
        </div>
    );
}

export default MonthlyCostSummary;
